import { React, useState, useEffect, createContext, useRef } from 'react';
import axios from 'axios';
import { Delete, Edit, PersonAddAlt1 } from '@mui/icons-material';
import Progress from '../Progress';
import EmployeeModifyOptions from '../forms/EmployeeModifyOptions';
import Popup from '../Popup';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { updateTableActions } from '../../store/update-table-slice';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../common/constants';
import { setLoaderMessage } from '../../utils/loaderManager';
import { TextField, Button, Stack, Typography } from '@mui/material';

let fetch_id = 0;
let fName = '';
let lName = '';
const EmpNameContext = createContext();

const maskEmail = (email) => {
  if (!email) return '';
  const [username, domain] = email.split('@');
  if (username.length <= 2) return `${'*'.repeat(username.length)}@${domain}`;
  return `${username[0]}${'*'.repeat(username.length - 2)}${username.slice(-1)}@${domain}`;
};

const maskPan = (pan) => {
  if (!pan) return '';
  if (pan.length <= 4) return '*'.repeat(pan.length);
  return `${'*'.repeat(pan.length - 4)}${pan.slice(-4)}`;
};

const maskMobile = (mobile) => {
  if (!mobile) return '';
  if (mobile.length <= 4) return '*'.repeat(mobile.length);
  return `${mobile.slice(0, 2)}${'*'.repeat(mobile.length - 6)}${mobile.slice(-4)}`;
};

const maskBankAccount = (account) => {
  if (!account) return '';
  if (account.length <= 4) return '*'.repeat(account.length);
  return `${'*'.repeat(account.length - 4)}${account.slice(-4)}`;
};

const maskUan = (uan) => {
  if (!uan) return '';
  if (uan.length <= 4) return '*'.repeat(uan.length);
  return `${'*'.repeat(uan.length - 4)}${uan.slice(-4)}`;
};

const maskPf = (pf) => {
  if (!pf) return '';
  if (pf.length <= 4) return '*'.repeat(pf.length);
  return `${'*'.repeat(pf.length - 4)}${pf.slice(-4)}`;
};

const dayAfter = (dateStr) => {
  if (!dateStr) return undefined;
  const d = new Date(String(dateStr).split('T')[0]);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

export default function EmployeeMasterTable({ data, loading: loadingProp, ex, onExListRefresh }) {
  const [openPopup, setOpenPopup] = useState(false);
  const [rejoinPopup, setRejoinPopup] = useState(false);
  const [rejoinTarget, setRejoinTarget] = useState(null);
  const [rejoinDate, setRejoinDate] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
  const dispatch = useDispatch();
  const updateEmployees = useSelector(state => state.updateTable.updateEmployees);
  const navigate = useNavigate();
  const scrollableBodyRef = useRef(null);
  const fixedBodyRef = useRef(null);

  async function getAllEmployees() {
    try {
      setLoaderMessage('Loading employees...');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/view-employees`);
      if (response.status === 200) {
        setEmployees(response.data);
      }
    } catch (e) {
      if (e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!data) getAllEmployees();
  }, [data]);

  useEffect(() => {
    if (updateEmployees && !data) {
      getAllEmployees();
      dispatch(updateTableActions.stopUpdateEmployees());
    }
  }, [updateEmployees, data]);

  const handleDelete = (deleteID) => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
    setLoaderMessage('Deleting employee...');
    axios.delete(`${process.env.REACT_APP_API_BASE_URL}/employee-master/delete-employee/` + deleteID)
      .then((response) => {
        if (response.status === 200) {
          getAllEmployees();
          setNotify({
            isOpen: true,
            message: 'Successfully Deleted!',
            type: 'error'
          });
        }
      })
      .catch(e => {
        if (e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      });
    dispatch(updateTableActions.startUpdateEmployees());
  };

  const handleCloseEditModal = () => {
    setOpenPopup(false);
  };

  const openRejoinDialog = (employee) => {
    setRejoinTarget(employee);
    setRejoinDate('');
    setRejoinPopup(true);
  };

  const handleRejoin = async () => {
    if (!rejoinTarget?.employee_id || !rejoinDate) {
      setNotify({ isOpen: true, message: 'Please select a rejoin date', type: 'error' });
      return;
    }
    setRejoinPopup(false);
    setLoaderMessage('Rejoining employee...');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/employee-master/rejoin-employee/${rejoinTarget.employee_id}`,
        { rejoin_date: rejoinDate }
      );
      if (response.status === 200) {
        setNotify({
          isOpen: true,
          message: response.data?.note
            ? `${response.data.message}. ${response.data.note}`
            : 'Employee rejoined successfully',
          type: 'success'
        });
        if (onExListRefresh) {
          onExListRefresh();
        }
        dispatch(updateTableActions.startUpdateEmployees());
      }
    } catch (e) {
      const msg = e.response?.data?.error_message || 'Failed to rejoin employee';
      if (e.response?.status === 401) {
        setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
        setTimeout(() => navigate('/'), 1000);
      } else {
        setNotify({ isOpen: true, message: msg, type: 'error' });
      }
    }
    setRejoinTarget(null);
    setRejoinDate('');
  };

  const tableData = data ? data : employees;
  const isLoading = typeof loadingProp === 'boolean' ? loadingProp : loading;
  const readonly = !!ex;
  const fixedBodyCellPadding = readonly ? '13px' : '8px';

  // Column widths
  const columnWidths = {
    edit: '70px',
    delete: '70px',
    firstName: '140px',
    lastName: '140px',
    birthday: '150px',
    joining: '150px',
    address: '250px',
    pinCode: '110px',
    company: '300px',
    gender: '150px',
    initials: '100px',
    marital: '140px',
    mobile: '150px',
    email: '250px',
    empCode: '150px',
    bank: '180px',
    ifsc: '140px',
    pan: '150px',
    pf: '150px',
    pfApplicable: '140px',
    carryLeave: '150px',
    carryDate: '150px',
    termDate: '150px',
    originalJoining: '150px',
    originalTermDate: '170px',
    vpfApplicable: '140px',
    vpfMode: '140px',
    vpf: '150px',
    pfLimit: '120px',
    uan: '150px',
    esi: '150px'
  };

  // Calculate widths for fixed and scrollable sections (4 fixed columns)
  const fixedColumnKeys = ['edit', 'delete', 'firstName', 'lastName'];
  const fixedColumnWidths = fixedColumnKeys.map(key => columnWidths[key]);
  const fixedColumnsTotalWidth = fixedColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);
  
  const scrollableColumnKeys = Object.keys(columnWidths).filter(key => !fixedColumnKeys.includes(key));
  const scrollableColumnWidths = scrollableColumnKeys.map(key => columnWidths[key]);
  const scrollableColumnsTotalWidth = scrollableColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);

  // Column names
  const fixedColumnNames = ['', '', 'First Name', 'Last Name'];
  const scrollableColumnNames = [
    'Employee B\'day',
    'Date of Joining',
    'Address',
    'Pin Code',
    'Company',
    'Employee Gender',
    'Initials',
    'Marital Status',
    'Mobile Number',
    'Email',
    'Employee Code',
    'Bank Account No',
    'IFSC Code',
    'PAN',
    'PF No',
    'PF Applicable',
    'Carry Over Leave',
    'Carry Over Date',
    'Termination Date',
    'Original DOJ',
    'Original Termination',
    'VPF Applicable',
    'VPF Mode',
    'VPF Value',
    'PF Limit',
    'UAN No.',
    'ESI No.'
  ];

  // Sync scroll for header and body
  useEffect(() => {
    const scrollableHeader = document.getElementById('scrollable-header-container');
    const scrollableBody = scrollableBodyRef.current;
    const fixedBody = fixedBodyRef.current;

    const syncScroll = () => {
      if (scrollableBody && scrollableHeader) {
        // Sync horizontal scroll for header
        scrollableHeader.scrollLeft = scrollableBody.scrollLeft;
      }
      if (scrollableBody && fixedBody) {
        // Move fixed columns up/down based on scroll position
        fixedBody.style.transform = `translateY(-${scrollableBody.scrollTop}px)`;
      }
    };

    scrollableBody?.addEventListener('scroll', syncScroll);

    return () => {
      scrollableBody?.removeEventListener('scroll', syncScroll);
    };
  }, [isLoading]);

  return (
    <>
      {isLoading ? <Progress /> :
        <div style={{ 
          position: 'relative', 
          height: '500px', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          
          {/* Header Container */}
          <Box 
            sx={{ 
              display: 'flex',
              position: 'sticky', 
              top: 0, 
              zIndex: 10,
              background: '#fff',
              flexShrink: 0,
              borderBottom: '2px solid #dee2e6',
              overflow: 'hidden'
            }}
          >
            {/* Fixed Header Columns */}
            <Box
              sx={{
                width: `${fixedColumnsTotalWidth}px`,
                flexShrink: 0,
                background: '#212529',
                color: 'white'
              }}
            >
              <table style={{ 
                width: '100%', 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table table-dark">
                <thead>
                  <tr>
                    {fixedColumnNames.map((name, index) => (
                      <th 
                        key={index}
                        style={{ 
                          width: fixedColumnWidths[index], 
                          padding: '13px', 
                          boxSizing: 'border-box', 
                          textAlign: index >= 2 ? 'left' : 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </Box>

            {/* Scrollable Header Columns */}
            <Box
              id="scrollable-header-container"
              sx={{ 
                flex: 1,
                overflowX: 'hidden',
                overflowY: 'hidden',
                background: '#212529',
                color: 'white'
              }}
            >
              <table style={{ 
                width: `${scrollableColumnsTotalWidth}px`, 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table table-dark">
                <thead>
                  <tr>
                    {scrollableColumnNames.map((name, index) => (
                      <th 
                        key={index}
                        style={{ 
                          width: scrollableColumnWidths[index], 
                          padding: '13px', 
                          boxSizing: 'border-box', 
                          textAlign: 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </Box>
          </Box>

          {/* Body Container - Modified for proper scrolling */}
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Fixed Body Columns - Wrapper that stays in place */}
            <Box
              sx={{ 
                width: `${fixedColumnsTotalWidth}px`,
                flexShrink: 0,
                background: '#fff',
                borderRight: '2px solid #dee2e6',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Inner container that moves with scroll */}
              <Box
                ref={fixedBodyRef}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  overflow: 'hidden'
                }}
              >
                <table style={{ 
                  width: '100%', 
                  tableLayout: 'fixed',
                  borderCollapse: 'collapse',
                  margin: 0
                }} className="table">
                  <tbody>
                    {tableData.map((employee) => (
                      <tr key={employee.employee_id} style={{ height: '42px' }}>
                        {!readonly ? (
                          <>
                            <td style={{ 
                              width: columnWidths.edit, 
                              padding: fixedBodyCellPadding, 
                              boxSizing: 'border-box', 
                              textAlign: 'center',
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              borderBottom: '1px solid #dee2e6'
                            }}>
                              <button 
                                type="button" 
                                className="btn btn-outline-primary btn-sm" 
                                onClick={() => { 
                                  setOpenPopup(true); 
                                  fetch_id = employee.employee_id; 
                                  fName = employee.first_name; 
                                  lName = employee.last_name 
                                }}
                              >
                                <Edit />
                              </button>
                            </td>
                            <td style={{ 
                              width: columnWidths.delete, 
                              padding: fixedBodyCellPadding, 
                              boxSizing: 'border-box', 
                              textAlign: 'center',
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              borderBottom: '1px solid #dee2e6'
                            }}>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setConfirmDialog({
                                  isOpen: true,
                                  title: 'Are you sure to delete this record?',
                                  subTitle: "You can't undo this operation",
                                  onConfirm: () => { handleDelete(employee.employee_id) }
                                })}
                              >
                                <Delete />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ 
                              width: columnWidths.edit, 
                              padding: fixedBodyCellPadding, 
                              boxSizing: 'border-box', 
                              textAlign: 'center',
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              borderBottom: '1px solid #dee2e6'
                            }}>
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                title="Rejoin employee"
                                onClick={() => openRejoinDialog(employee)}
                              >
                                <PersonAddAlt1 />
                              </button>
                            </td>
                            <td style={{ 
                              width: columnWidths.delete, 
                              padding: fixedBodyCellPadding, 
                              boxSizing: 'border-box', 
                              textAlign: 'center',
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              borderBottom: '1px solid #dee2e6'
                            }}></td>
                          </>
                        )}
                        <td style={{ 
                          width: columnWidths.firstName, 
                          padding: fixedBodyCellPadding, 
                          boxSizing: 'border-box', 
                          textAlign: 'left',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          {employee.first_name}
                        </td>
                        <td style={{ 
                          width: columnWidths.lastName, 
                          padding: fixedBodyCellPadding, 
                          boxSizing: 'border-box', 
                          textAlign: 'left',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          {employee.last_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>

            {/* Scrollable Body Columns - Has the main scroll */}
            <Box
              ref={scrollableBodyRef}
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                background: '#fff'
              }}
            >
              <table style={{ 
                width: `${scrollableColumnsTotalWidth}px`, 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table">
                <tbody>
                  {tableData.map((employee) => (
                    <tr key={employee.employee_id} style={{ height: '42px' }}>
                      <td style={{ 
                        width: columnWidths.birthday, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.birth_date ? formatDate(employee.birth_date) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.joining, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.date_of_joining ? formatDate(employee.date_of_joining) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.address, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.address || '-'}</td>
                      <td style={{ 
                        width: columnWidths.pinCode, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.pin_code ?? '-'}</td>
                      <td style={{ 
                        width: columnWidths.company, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.company_name || '-'}</td>
                      <td style={{ 
                        width: columnWidths.gender, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.gender || '-'}</td>
                      <td style={{ 
                        width: columnWidths.initials, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.initial || '-'}</td>
                      <td style={{ 
                        width: columnWidths.marital, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.marital_status || '-'}</td>
                      <td style={{ 
                        width: columnWidths.mobile, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.mobile_no ? maskMobile(employee.mobile_no) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.email, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.email ? maskEmail(employee.email) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.empCode, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.employee_code ?? '-'}</td>
                      <td style={{ 
                        width: columnWidths.bank, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.ICICI_account_no ? maskBankAccount(employee.ICICI_account_no) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.ifsc, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee.ifsc_code || '-'}</td>
                      <td style={{ 
                        width: columnWidths.pan, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.pan_card_no ? maskPan(employee.pan_card_no) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.pf, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.pf_number ? maskPf(employee.pf_number) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.pfApplicable, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.pf_applicable === 1 ? 'Yes' : 'No'}</td>
                      <td style={{ 
                        width: columnWidths.carryLeave, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.leave_carryover ?? '-'}</td>
                      <td style={{
                        width: columnWidths.carryDate,
                        padding: '13px',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.carryover_date ? formatDate(String(employee.carryover_date).split('T')[0]) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.termDate, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.date_of_termination ? formatDate(employee.date_of_termination) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.originalJoining, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.original_date_of_joining ? formatDate(employee.original_date_of_joining) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.originalTermDate, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.original_date_of_termination ? formatDate(employee.original_date_of_termination) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.vpfApplicable, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{Number(employee?.vpf_applicable) === 1 ? 'Yes' : 'No'}</td>
                      <td style={{ 
                        width: columnWidths.vpfMode, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{Number(employee?.vpf_applicable) === 1
                        ? (employee?.vpf_deduction_type === 'A' ? 'Amount' : 'Percentage')
                        : '-'}</td>
                      <td style={{ 
                        width: columnWidths.vpf, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{Number(employee?.vpf_applicable) === 1
                        ? (employee?.vpf_deduction_type === 'A'
                          ? (employee?.VPFFixedAmount ?? '-')
                          : (employee?.VPFPercentage ?? '-'))
                        : '-'}</td>
                      <td style={{ 
                        width: columnWidths.pfLimit, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.PFLimitRs ?? '-'}</td>
                      <td style={{ 
                        width: columnWidths.uan, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.UANNumber ? maskUan(employee.UANNumber) : '-'}</td>
                      <td style={{ 
                        width: columnWidths.esi, 
                        padding: '13px', 
                        boxSizing: 'border-box', 
                        textAlign: 'center',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{employee?.ESINumber ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* Popups and Notifications */}
          <Popup
            title={'Update Record for ' + fName + ' ' + lName}
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
          >
            <EmpNameContext.Provider value={{ 
              eID: fetch_id, 
              fName: fName, 
              lName: lName,
              isInternApprentice: tableData.find(e => e.employee_id === fetch_id)?.is_intern_apprentice === 1 
            }}>
              <EmployeeModifyOptions
                title="Modify Employee"
                fetch_id={fetch_id}
                onUpdated={getAllEmployees}
              />
            </EmpNameContext.Provider>
          </Popup>
          <Notification
            notify={notify}
            setNotify={setNotify}
          />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <Popup
            title={rejoinTarget ? `Rejoin ${rejoinTarget.first_name} ${rejoinTarget.last_name}` : 'Rejoin Employee'}
            openPopup={rejoinPopup}
            setOpenPopup={setRejoinPopup}
            maxWidth="xs"
          >
            <Stack spacing={2} sx={{ p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date of joining will be set to the rejoin date. Add a new salary/CTC with effective date on or after rejoin, then process payroll.
              </Typography>
              {rejoinTarget?.date_of_termination && (
                <Typography variant="body2">
                  Last working day: {formatDate(rejoinTarget.date_of_termination)}
                </Typography>
              )}
              <TextField
                label="Rejoin Date"
                type="date"
                value={rejoinDate}
                onChange={(e) => setRejoinDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: rejoinTarget?.date_of_termination
                    ? dayAfter(rejoinTarget.date_of_termination)
                    : undefined
                }}
                fullWidth
                required
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setRejoinPopup(false)}>Cancel</Button>
                <Button variant="contained" color="success" onClick={handleRejoin}>Rejoin</Button>
              </Stack>
            </Stack>
          </Popup>
        </div>
      }
    </>
  );
}

export { EmpNameContext };